/**
 * DataAgent Chat Store
 *
 * @package DataAgent
 * @since 1.1.0
 */

import { defineStore } from 'pinia';

export const useChatStore = defineStore('chat', {
  state: () => ({
    // Messages
    messages: [],
    
    // Current message being typed
    currentMessage: '',

    // Active tab in chat
    activeChatTab: 'chat',
    
    // Loading states
    isLoading: false,
    isSending: false,
    
    // Quota information
    quota: {
      used: 20,
      total: 3000,
      resetDate: null,
      isPremium: false
    },
    
    // Chat settings
    settings: {
      model: '',
      provider: '',
      hasApiKey: false,
      logo: '../../img/icon-256x256.png',
      apiUrl: '',
      settingsUrl: '',
      upgradeUrl: '',
      nonce: ''
    },
    
    // Prompt Examples (will be loaded from backend)
    promptExamples: [],
    
    // Labels for i18n (will be loaded from backend)
    labels: {},
    
    // UI States
    showPromptExamples: false,
    
    // Notices
    notices: [],
  }),
  
  getters: {
    /**
     * Get quota percentage
     * 
     * @returns {number} Percentage used
     */
    getQuotaPercentage: (state) => {
      return Math.round((state.quota.used / state.quota.total) * 100 * 100) / 100;
    },
    
    /**
     * Get quota usage text
     * 
     * @returns {string} Usage text
     */
    getQuotaUsageText: (state) => {
      return `${state.quota.used}/${state.quota.total}`;
    }
  },
  
  actions: {
    /**
     * Initialize chat data from global variables
     */
    init() {
      // Load from WordPress global variable if available
      if (typeof window.DATAGENT_PLUGIN_CHAT !== 'undefined') {
        const data = window.DATAGENT_PLUGIN_CHAT;
        
        // Load settings
        this.settings.logo = data.logo || '../../img/icon-256x256.png';
        this.settings.hasApiKey = data.hasApiKey || false;
        this.settings.apiUrl = data.api_url || '';
        this.settings.settingsUrl = data.settings_url || '';
        this.settings.upgradeUrl = data.upgrade_url || '';
        this.settings.nonce = data.nonce || '';
        
        // Load provider settings
        if (data.settings) {
          this.settings.provider = data.settings.provider || '';
          this.settings.model = data.settings.model || '';
        }
        
        // Load quota information
        if (data.quota) {
          this.quota.used = data.quota.used || 0;
          this.quota.total = data.quota.total || 1000;
          this.quota.resetDate = data.quota.resetDate || null;
          this.quota.isPremium = data.quota.isPremium || false;
        }
        
        // Load prompt examples from backend
        if (data.promptExamples && Array.isArray(data.promptExamples)) {
          this.promptExamples = data.promptExamples;
        }
        
        // Store labels for i18n
        if (data.labels) {
          this.labels = data.labels;
        }
      }
      
      // Fallback to settings global if chat global not available
      if (typeof window.DATAGENT_PLUGIN_SETTINGS !== 'undefined') {
        const settings = window.DATAGENT_PLUGIN_SETTINGS.settings;
        
        if (settings && !this.settings.provider) {
          this.settings.provider = settings.provider || '';
          this.settings.model = settings[settings.provider]?.model || '';
        }
      }
    },

    /**
     * Set the active chat tab
     *
     * @param {string} tab Tab name
     */
    setActiveChatTab(tab) {
      this.activeChatTab = tab;
    },
    
    /**
     * Send a new message
     * 
     * @param {Object} messageData Message data with original and formatted content
     */
    async sendMessage(messageData) {
      if (!messageData.original.trim()) return;
      
      try {
        // Add user message
        this.messages.push({
          id: Date.now(),
          type: 'user',
          content: messageData.formatted,
          timestamp: new Date()
        });
        
        // Set loading state
        this.isSending = true;
        
        // If no API key is configured, show connection prompt
        if (!this.settings.hasApiKey) {
          setTimeout(() => {
            const noApiKeyMessage = this.labels.no_api_key || 'Please configure your AI API key in the settings to start chatting.';
            const settingsLink = this.settings.settingsUrl || (window.location.origin + '/wp-admin/admin.php?page=dataagent-settings');
            this.messages.push({
              id: Date.now() + 1,
              type: 'ai',
              content: `${noApiKeyMessage} <a href="${settingsLink}">settings</a>`,
              isError: true,
              timestamp: new Date()
            });
            this.isSending = false;
          }, 500);
          return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        
        // Make API call
        const response = await fetch(this.settings.apiUrl + 'dataagent/v1/chat/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': this.settings.nonce
          },
          body: JSON.stringify({
            message: messageData.original,
            resource: urlParams.get('resource'),
            id: urlParams.get('id'),
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          // Add AI response
          this.messages.push({
            id: Date.now() + 2,
            type: 'ai',
            content: result.message,
            timestamp: new Date()
          });
          
          // Update quota if not premium
          if (!this.quota.isPremium) {
            this.quota.used += 1;
          }
        } else {
          throw new Error(result.message || 'Failed to send message');
        }
      } catch (error) {
        // Add error message
        this.messages.push({
          id: Date.now() + 3,
          type: 'ai',
          content: 'Sorry, I encountered an error: ' + error.message,
          isError: true,
          timestamp: new Date()
        });
      } finally {
        this.isSending = false;
      }
    },
    
    /**
     * Set current message being typed
     * 
     * @param {string} message Message content
     */
    setCurrentMessage(message) {
      this.currentMessage = message;
    },
    
    /**
     * Clear current message
     */
    clearCurrentMessage() {
      this.currentMessage = '';
    },
    
    /**
     * Show prompt examples modal
     */
    showPromptExamplesModal() {
      this.showPromptExamples = true;
    },
    
    /**
     * Hide prompt examples modal
     */
    hidePromptExamplesModal() {
      this.showPromptExamples = false;
    },
    
    /**
     * Use example prompt
     * 
     * @param {string} promptText Prompt text to use
     */
    useExamplePrompt(promptText) {
      this.currentMessage = promptText;
      this.hidePromptExamplesModal();
    },
    
    /**
     * Clear all messages
     */
    clearMessages() {
      this.messages = [];
    },
    
    /**
     * Add a notice
     * 
     * @param {string} message Notice message
     * @param {string} type Notice type (success, error, warning, info)
     */
    addNotice(message, type = 'info') {
      const notice = {
        id: Date.now(),
        message,
        type,
        timestamp: new Date()
      };
      
      this.notices.push(notice);
      
      // Auto remove after 5 seconds
      setTimeout(() => {
        this.removeNotice(notice.id);
      }, 5000);
    },
    
    /**
     * Remove a notice
     * 
     * @param {number} id Notice ID
     */
    removeNotice(id) {
      this.notices = this.notices.filter(notice => notice.id !== id);
    },
    
    /**
     * Get quota level based on percentage
     * 
     * @returns {string} Quota level (low, medium, high)
     */
    getQuotaLevel() {
      const percentage = this.getQuotaPercentage;
      if (percentage < 50) return 'low';
      if (percentage < 80) return 'medium';
      return 'high';
    },
    
    /**
     * Load chat settings and quota from server (legacy support)
     */
    loadChatData() {
      this.init();
    }
  }
});
